import { Box, Button, HStack, Icon, Text, VStack, useToast } from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TbInfoCircle } from 'react-icons/tb';
import type { QueryObserverResult, RefetchOptions } from '@tanstack/react-query';
import type { QuoteRequirement, QuotingSettings } from '../../../../hooks/useGetSettings';
import { useGetLocations } from '../../../../hooks/useGetLocations';
import { useUpdateSettings } from '../../../../hooks/useUpdateSettings';
import {
    SettingsCard,
    SettingsCheckTable,
    SettingsFlow,
    SettingsFlowSplit,
    SettingsRow,
    useSettingsDetailCrumbs,
} from '../../../../components/settings/primitives';
import VendorSearchField from '../vendors/VendorSearchField';
import type { SupplierSearchResult } from '../../../../hooks/useSearchSuppliers';
import { resolveLocationList, toLocationIds } from '../../../../components/settings/locationListValue';
import type { MakeOption } from '../../../../components/settings/makesListValue';

/** Sentinel ids for the "All …" rows above each pick list. */
const ALL_LOCATIONS_ROW = '__all_locations__';
const ALL_MAKES_ROW = '__all_makes__';

interface RequoteDraft {
    supplierId: string;
    name: string;
    makeIds: Array<string>;
    locationIds: Array<string> | 'all';
}

export interface RequoteRuleDetailSectionProps {
    /** The rule's index in the requote array, or 'new' for the add case. */
    ruleId: string;
    quoting: QuotingSettings;
    oemOptions: MakeOption[];
    refetch?: (options?: RefetchOptions) => Promise<QueryObserverResult<unknown, Error>>;
}

/**
 * Add or edit one re-quote rule, full screen: the vendor and the makes it
 * covers on the left, the locations it applies at on the right. One step, the
 * same split the vendor, carrier and OEM screens use, so adding and editing
 * are the same screen.
 *
 * Saving writes the whole quoting subdocument through POST /settings/update,
 * which is how this endpoint works, so the rule is merged into the current
 * list here rather than staged on the section's draft.
 */
const RequoteRuleDetailSection = ({
    ruleId,
    quoting,
    oemOptions,
    refetch,
}: RequoteRuleDetailSectionProps) => {
    const navigate = useNavigate();
    const toast = useToast();
    const updateSettings = useUpdateSettings();
    const { data: locationsData } = useGetLocations();

    const rules = useMemo(() => quoting.requote ?? [], [quoting.requote]);
    const isAddMode = ruleId === 'new';
    const ruleIndex = isAddMode ? -1 : Number(ruleId);
    const rule =
        !isAddMode && Number.isInteger(ruleIndex) ? rules[ruleIndex] : undefined;

    // Real shops only; the synthetic 'all' entry is the table's header row.
    const locations = useMemo(
        () => locationsData?.locations?.filter((location) => location._id !== 'all') ?? [],
        [locationsData],
    );

    const makeOptions = useMemo(
        () =>
            oemOptions
                .filter((option) => option.name && option.name.trim() !== '')
                .sort((a, b) => a.name.localeCompare(b.name)),
        [oemOptions],
    );

    const [draft, setDraft] = useState<RequoteDraft>(() => ({
        supplierId: rule?.supplierId ?? '',
        name: rule?.name ?? '',
        makeIds: rule?.makes.map((make) => make._id) ?? [],
        locationIds: rule?.locations === 'all' ? 'all' : rule?.locations.map((l) => l._id) ?? [],
    }));

    // The saved locations carry titles that only the locations list can line up
    // with real shops, so the picks are resolved once that query lands.
    useEffect(() => {
        if (!rule || locations.length === 0) return;
        setDraft((prev) => ({
            ...prev,
            locationIds: toLocationIds(resolveLocationList(rule.locations, locations)),
        }));
        // Seeded from the saved rule once the shops are known; later edits own it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locations.length]);

    // An out-of-range index has nothing to edit, so fall back to the list.
    useEffect(() => {
        if (!isAddMode && !rule) navigate('/settings/quoting', { replace: true });
    }, [isAddMode, rule, navigate]);

    useSettingsDetailCrumbs([
        { label: isAddMode ? 'Add re-quote rule' : draft.name || 'Re-quote rule' },
    ]);

    const closeFlow = () => navigate('/settings/quoting');

    const hasVendor = draft.supplierId.length > 0;
    const canSave =
        hasVendor &&
        draft.makeIds.length > 0 &&
        (draft.locationIds === 'all' || draft.locationIds.length > 0);

    const handleSelectSupplier = (supplier: SupplierSearchResult) => {
        setDraft((prev) => ({ ...prev, supplierId: supplier._id, name: supplier.title }));
    };

    const allMakesChecked =
        makeOptions.length > 0 && makeOptions.every((option) => draft.makeIds.includes(option._id));
    const allLocationsChecked =
        draft.locationIds === 'all' ||
        (locations.length > 0 &&
            locations.every((location) => draft.locationIds.includes(location._id as string)));

    const handleToggleMake = (id: string, checked: boolean) => {
        if (id === ALL_MAKES_ROW) {
            setDraft((prev) => ({
                ...prev,
                makeIds: checked ? makeOptions.map((option) => option._id) : [],
            }));
            return;
        }
        setDraft((prev) => ({
            ...prev,
            makeIds: checked
                ? Array.from(new Set([...prev.makeIds, id]))
                : prev.makeIds.filter((makeId) => makeId !== id),
        }));
    };

    const handleToggleLocation = (id: string, checked: boolean) => {
        if (id === ALL_LOCATIONS_ROW) {
            setDraft((prev) => ({ ...prev, locationIds: checked ? 'all' : [] }));
            return;
        }
        setDraft((prev) => {
            // Leaving "All Locations" starts from every shop, so unchecking one
            // reads the way it looks rather than wiping the assignment.
            const base =
                prev.locationIds === 'all'
                    ? locations.map((location) => location._id as string)
                    : prev.locationIds;
            return {
                ...prev,
                locationIds: checked
                    ? Array.from(new Set([...base, id]))
                    : base.filter((locationId) => locationId !== id),
            };
        });
    };

    const writeRules = (
        nextRules: Array<QuoteRequirement>,
        successTitle: string,
        successDescription: string,
        errorTitle: string,
    ) => {
        updateSettings.mutate(
            { quoting: { ...quoting, requote: nextRules } },
            {
                onSuccess: () => {
                    toast({
                        title: successTitle,
                        description: successDescription,
                        status: 'success',
                        duration: 3000,
                        isClosable: true,
                    });
                    refetch?.();
                    closeFlow();
                },
                onError: (error) => {
                    toast({
                        title: errorTitle,
                        description: error.message || 'Please try again.',
                        status: 'error',
                        duration: 5000,
                        isClosable: true,
                    });
                },
            },
        );
    };

    const handleSave = () => {
        if (!canSave) return;

        const nextRule: QuoteRequirement = {
            supplierId: draft.supplierId,
            name: draft.name,
            makes: makeOptions
                .filter((option) => draft.makeIds.includes(option._id))
                .map((option) => ({ _id: option._id, title: option.name })),
            locations:
                draft.locationIds === 'all'
                    ? 'all'
                    : locations
                          .filter((location) =>
                              (draft.locationIds as Array<string>).includes(location._id as string),
                          )
                          .map((location) => ({
                              _id: location._id as string,
                              title: location.title,
                          })),
        };

        const nextRules = rule
            ? rules.map((candidate, index) => (index === ruleIndex ? nextRule : candidate))
            : [...rules, nextRule];

        writeRules(
            nextRules,
            isAddMode ? 'Re-quote rule added' : 'Re-quote rule updated',
            `${draft.name} has been saved.`,
            isAddMode ? 'Could not add rule' : 'Could not update rule',
        );
    };

    const handleRemove = () => {
        if (!rule) return;
        writeRules(
            rules.filter((_, index) => index !== ruleIndex),
            'Re-quote rule removed',
            `${rule.name} has been removed.`,
            'Could not remove rule',
        );
    };

    if (!isAddMode && !rule) return null;

    const left = (
        // A flex column, not a scrolling box: the makes table takes whatever
        // height is left over and scrolls on its own.
        <VStack align="stretch" spacing={6} flex={1} minH={0} px={6} pb={6} overflow="hidden">
            {hasVendor ? (
                <SettingsCard title="Vendor">
                    <SettingsRow
                        label={draft.name}
                        control={
                            isAddMode ? (
                                <Button
                                    size="xs"
                                    variant="ghost"
                                    colorScheme="gray"
                                    onClick={() =>
                                        setDraft((prev) => ({
                                            ...prev,
                                            supplierId: '',
                                            name: '',
                                        }))
                                    }
                                >
                                    Change
                                </Button>
                            ) : undefined
                        }
                    />
                </SettingsCard>
            ) : (
                // Not a SettingsCard: the card clips its content, which would
                // cut off the search results hanging below the input.
                <Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.800" mb={1}>
                        Vendor name
                    </Text>
                    <Text fontSize="sm" color="gray.500" mb={2.5}>
                        Search the suppliers Yaxle knows and pick one.
                    </Text>
                    <VendorSearchField onSelect={handleSelectSupplier} autoFocus />
                    <HStack spacing={2} align="flex-start" mt={4}>
                        <Icon
                            as={TbInfoCircle as React.ElementType}
                            boxSize={4}
                            color="gray.400"
                            mt="2px"
                            flexShrink={0}
                        />
                        <Text fontSize="sm" color="gray.500">
                            This rule quotes an additional 2 times, or until it sees this vendor
                            quote the parts.
                        </Text>
                    </HStack>
                </Box>
            )}

            {hasVendor && (
                <Box flex={1} minH={0} display="flex" flexDirection="column">
                    <Text fontSize="sm" fontWeight="medium" color="gray.800" mb={1} flexShrink={0}>
                        Makes
                    </Text>
                    <Text fontSize="sm" color="gray.500" mb={2.5} flexShrink={0}>
                        Which makes this vendor is re-quoted for.
                    </Text>
                    <SettingsCheckTable
                        fill
                        columnLabel="MAKE"
                        checkLabel="Include"
                        emptyMessage="No makes available"
                        rows={[
                            {
                                id: ALL_MAKES_ROW,
                                label: 'All Makes',
                                isChecked: allMakesChecked,
                                isEmphasis: true,
                            },
                            ...makeOptions.map((option) => ({
                                id: option._id,
                                label: option.name,
                                isChecked: draft.makeIds.includes(option._id),
                            })),
                        ]}
                        onToggle={handleToggleMake}
                    />
                </Box>
            )}
        </VStack>
    );

    return (
        <SettingsFlow
            steps={[{ id: 'rule', label: 'Re-quote rule' }]}
            activeStepId="rule"
            onCancel={closeFlow}
            onSave={handleSave}
            saveLabel={isAddMode ? 'Add Rule' : 'Save Rule'}
            isSaveDisabled={!canSave}
            isSaving={updateSettings.isPending}
            actions={
                rule ? (
                    <Button
                        size="sm"
                        variant="outline"
                        colorScheme="red"
                        onClick={handleRemove}
                        isDisabled={updateSettings.isPending}
                    >
                        Remove Rule
                    </Button>
                ) : undefined
            }
        >
            <SettingsFlowSplit
                leftWidth="50%"
                leftTitle={isAddMode ? 'Add rule' : 'Rule'}
                left={left}
                isLeftFlush
                // The locations pane waits on a vendor, the same way the vendor
                // and carrier screens wait before offering their tables.
                rightTitle={hasVendor ? 'Applicable locations' : undefined}
                rightDescription={
                    hasVendor ? 'Which of your shops this rule applies at.' : undefined
                }
                right={
                    hasVendor ? (
                        <Box flex={1} minH={0} px={6} pb={6} display="flex" flexDirection="column">
                            <SettingsCheckTable
                                fill
                                columnLabel="LOCATION"
                                checkLabel="Applies"
                                emptyMessage="No locations configured yet"
                                rows={[
                                    {
                                        id: ALL_LOCATIONS_ROW,
                                        label: 'All Locations',
                                        isChecked: allLocationsChecked,
                                        isEmphasis: true,
                                    },
                                    ...locations.map((location) => ({
                                        id: location._id as string,
                                        label: location.title,
                                        isChecked:
                                            draft.locationIds === 'all' ||
                                            draft.locationIds.includes(location._id as string),
                                    })),
                                ]}
                                onToggle={handleToggleLocation}
                            />
                        </Box>
                    ) : null
                }
                isRightFlush
            />
        </SettingsFlow>
    );
};

export default RequoteRuleDetailSection;
